import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";

import { cn } from "../../lib/utils";
import { isSelectOverlayTarget } from "./overlay-interactions";
import { overlayScrimClassName } from "./overlay-scrim";
import { bottomSheetGrabberClassName } from "./bottom-sheet-surface";
import { MOBILE_SHEET_HOST_GROW_CLASS, MobileSheetHost } from "./mobile-sheet-host";

function Drawer({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Root>) {
  return <DrawerPrimitive.Root data-slot='drawer' {...props} />;
}

function DrawerTrigger({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Trigger>) {
  return <DrawerPrimitive.Trigger data-slot='drawer-trigger' {...props} />;
}

function DrawerPortal({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Portal>) {
  return (
    <DrawerPrimitive.Portal {...props}>
      <div data-slot='drawer-portal'>{props.children}</div>
    </DrawerPrimitive.Portal>
  );
}

function DrawerClose({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Close>) {
  return <DrawerPrimitive.Close data-slot='drawer-close' {...props} />;
}

function DrawerOverlay({ className, ...props }: React.ComponentProps<typeof DrawerPrimitive.Overlay>) {
  return (
    <DrawerPrimitive.Overlay
      data-slot='drawer-overlay'
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50",
        overlayScrimClassName,
        className,
      )}
      {...props}
    />
  );
}

function DrawerContent({
  className,
  children,
  onInteractOutside,
  onPointerDownOutside,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Content>) {
  return (
    <DrawerPortal>
      <DrawerOverlay />
      <DrawerPrimitive.Content
        data-slot='drawer-content'
        className={cn(
          "group/drawer-content bg-card fixed z-50 flex h-auto flex-col text-sm shadow-[var(--shadow-dialog)]",
          "data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-24 data-[vaul-drawer-direction=top]:max-h-[80vh] data-[vaul-drawer-direction=top]:rounded-b-lg data-[vaul-drawer-direction=top]:border-b",
          "data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:mt-24 data-[vaul-drawer-direction=bottom]:max-h-[80vh] data-[vaul-drawer-direction=bottom]:overflow-hidden",
          // The shared iOS corners (bottomSheetCornerClassName in bottom-sheet-surface.ts), written out in full
          // so Tailwind generates the variant.
          "data-[vaul-drawer-direction=bottom]:rounded-t-[2rem]",
          MOBILE_SHEET_HOST_GROW_CLASS,
          "data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:w-3/4 data-[vaul-drawer-direction=right]:border-l data-[vaul-drawer-direction=right]:sm:max-w-sm",
          "data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:w-3/4 data-[vaul-drawer-direction=left]:border-r data-[vaul-drawer-direction=left]:sm:max-w-sm",
          className,
        )}
        onInteractOutside={(event) => {
          if (isSelectOverlayTarget(event.target)) {
            event.preventDefault();
            return;
          }

          onInteractOutside?.(event);
        }}
        onPointerDownOutside={(event) => {
          if (isSelectOverlayTarget(event.target)) {
            event.preventDefault();
            return;
          }

          onPointerDownOutside?.(event);
        }}
        {...props}
      >
        <div aria-hidden className={cn(bottomSheetGrabberClassName, "hidden group-data-[vaul-drawer-direction=bottom]/drawer-content:block")} />
        <MobileSheetHost>{children}</MobileSheetHost>
      </DrawerPrimitive.Content>
    </DrawerPortal>
  );
}

function DrawerHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot='drawer-header'
      className={cn(
        "flex flex-col gap-0.5 p-4 group-data-[vaul-drawer-direction=bottom]/drawer-content:text-center group-data-[vaul-drawer-direction=top]/drawer-content:text-center md:gap-1.5 md:text-left",
        className,
      )}
      {...props}
    />
  );
}

function DrawerFooter({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot='drawer-footer' className={cn("mt-auto flex flex-col gap-2 p-4", className)} {...props} />;
}

function DrawerTitle({ className, ...props }: React.ComponentProps<typeof DrawerPrimitive.Title>) {
  return (
    <DrawerPrimitive.Title
      data-slot='drawer-title'
      className={cn("text-foreground font-medium", className)}
      {...props}
    />
  );
}

function DrawerDescription({ className, ...props }: React.ComponentProps<typeof DrawerPrimitive.Description>) {
  return (
    <DrawerPrimitive.Description
      data-slot='drawer-description'
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

const DrawerHandle = DrawerPrimitive.Handle;

export {
  Drawer,
  DrawerTrigger,
  DrawerPortal,
  DrawerClose,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerHandle,
  DrawerTitle,
  DrawerDescription,
};
